import { Provider } from '@/features/game-context/types';
import { fusionEventMapper } from '@/features/game-window-integration/helpers/fusion/handlers';
import { loadFusionScript } from '@/features/game-window-integration/helpers/fusion/loader';
import { FusionInitArgs } from '@/features/game-window-integration/providers/fusion/types';
import {
  DdEventCategory,
  DdEventType,
  Logger,
} from '@/features/logging/logger';

export const initFusion = async (
  args: FusionInitArgs,
): Promise<FusionGlobal | void> => {
  if (!args?.region || !args?.gameCode) {
    Logger.logRumError({
      name: '[Fusion Inclusion] Missing Args',
      description:
        'Missing required args (region and/or gameCode) for Fusion init',
      useCase: DdEventCategory.ProviderIntegration,
      eventType: DdEventType.Error,
      context: { args, provider: Provider.FUSION },
    });
    return undefined;
  }

  try {
    await loadFusionScript(args.region);

    const { fusion } = window;
    if (!fusion) {
      throw new Error('Fusion library not available after load');
    }

    const fusionInstance = await new Promise<FusionGlobal>(
      (resolve, reject) => {
        const onSuccess = () => {
          fusionEventMapper.forEach(([event, handler]) => {
            fusion.addListener(event, handler);
          });
          Logger.logRumAction({
            name: '[Fusion Inclusion] Successfully Initialized',
            useCase: DdEventCategory.ProviderIntegration,
            eventType: DdEventType.Success,
            context: {
              region: args.region,
              gameCode: args.gameCode,
              provider: Provider.FUSION,
            },
          });
          resolve(fusion);
        };

        const onError = (err: unknown) => reject(err);

        try {
          fusion.init({
            gameCode: args.gameCode,
            onSuccess,
            onError,
          });
        } catch (e) {
          onError(e);
        }
      },
    );

    return fusionInstance;
  } catch (error) {
    Logger.logRumError({
      name: '[Fusion Inclusion] Initialization Failure',
      useCase: DdEventCategory.ProviderIntegration,
      eventType: DdEventType.Error,
      context: {
        error,
        region: args.region,
        gameCode: args.gameCode,
        provider: Provider.FUSION,
      },
    });
    return undefined;
  }
};
